/**
 * WriteoffSlice Edit Tracking Reducers
 * 
 * Reducers for managing edit state and dirty tracking.
 */

import { PayloadAction, CaseReducer } from '@reduxjs/toolkit';
import { WriteoffSliceState } from '../state';
import { addEditedField, createBackupManager } from '../../../shared/sliceUtils';

// Backup manager for clearing on save
const backupManager = createBackupManager<unknown>('writeoff_backup');

// =============================================================================
// EDIT TRACKING REDUCERS
// =============================================================================

/**
 * Mark a specific field as edited
 */
export const markFieldAsEdited: CaseReducer<WriteoffSliceState, PayloadAction<string>> = (
  state,
  action
) => {
  addEditedField(state, action.payload);
};

/**
 * Clear all edited fields tracking
 */
export const clearEditedFields: CaseReducer<WriteoffSliceState> = (state) => {
  state.editedFields = [];
  state.isEdited = false;
};

/**
 * Mark as saved - clears edit tracking and backup
 */
export const markAsSaved: CaseReducer<WriteoffSliceState> = (state) => {
  state.isEdited = false;
  state.editedFields = [];
  backupManager.clear();
};

/**
 * Discard changes - clears edit tracking (UI should refetch from API)
 */
export const discardChanges: CaseReducer<WriteoffSliceState> = (state) => {
  state.isEdited = false;
  state.editedFields = [];
};

// =============================================================================
// VALIDATION REDUCERS
// =============================================================================

/**
 * Set a validation error for a specific field
 */
export const setValidationError: CaseReducer<
  WriteoffSliceState,
  PayloadAction<{ field: string; message: string }>
> = (state, action) => {
  state.validationErrors[action.payload.field] = action.payload.message;
};

/**
 * Clear a validation error for a specific field
 */
export const clearValidationError: CaseReducer<WriteoffSliceState, PayloadAction<string>> = (
  state,
  action
) => {
  delete state.validationErrors[action.payload];
};

/**
 * Clear all validation errors
 */
export const clearAllValidationErrors: CaseReducer<WriteoffSliceState> = (state) => {
  state.validationErrors = {};
};
